<template>
  <q-drawer
    ref="wsDrawerEl"
    v-ws-autofocus="'.q-drawer'"
    class="ws-drawer ws-typography"
    :value="value"
    v-bind="$attrs"
    side="right"
    overlay
    elevated
    :no-swipe-open="$q.platform.is.desktop"
    :no-swipe-close="$q.platform.is.desktop"
    no-swipe-backdrop
    behavior="mobile"
    :width="560"
    @input="handleDrawerVisibility"
  >
    <div
      class="ws-drawer-inner fit column no-wrap"
      :class="{ 'full-height-content': fullHeightContent }"
    >
      <div
        v-if="$slots.header"
        class="header"
      >
        <slot name="header"></slot>
      </div>
      <div
        v-else-if="title"
        class="header"
      >
        <div>
          <h5
            v-if="title"
            class="q-mb-xs"
          >
            {{ $t(title) }}
          </h5>
          <slot name="subtitle"></slot>
        </div>

        <div
          v-if="value"
          class="close-wrap"
        >
          <ws-button
            :type="WsButtonEnum.Type.GHOST"
            icon="close"
            @click="handleDrawerVisibility(false)"
          />
        </div>
      </div>
      <div class="content ws-invisible-scrollbar">
        <slot name="content"></slot>
        <slot></slot><!-- default -->
      </div>
      <div
        v-if="$slots.footer"
        class="footer full-width"
      >
        <slot name="footer"></slot>
      </div>
    </div>
  </q-drawer>
</template>

<script lang="ts">
import {QDrawer} from 'quasar';
import { WsButton, WsButtonEnum } from '@odin/components-vue2';
import {omit} from 'lodash-es';

import { defineComponent, onMounted, computed, ref, onBeforeUnmount } from 'vue';

export default defineComponent({
  name: 'WsDrawer',
  components: {QDrawer, WsButton},
  inheritAttrs: false,

  props: {
    title: {
      type: String,
      default: '',
    },
    value: {
      type: Boolean,
    },
    fullHeightContent: {
      type: Boolean,
    },
    preventClose: {
      type: Boolean,
    },
    closeOnEscKey: {
      type: Boolean,
      default: true
    }
  },

  setup(props, vm)  {

    const listeners = computed(() => omit({...vm.listeners}, 'input'));
    const wsDrawerEl = ref<QDrawer | null>(null);

    const handleOnKeydown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || !props.closeOnEscKey || props.preventClose) {
        return;
      }

      const isOpened = wsDrawerEl.value?.value;

      if (!isOpened) {
        return;
      }

      wsDrawerEl.value?.hide();
    };

    const handleDrawerVisibility = (state = false) =>  {
      vm.emit('input', (!state && props.preventClose) || state);
    };

    onMounted(() => {
      vm.emit('mounted');
      // We use `behavior="mobile"` as a QDrawer prop which gives us a nice backdrop which can be clicked to close
      // the drawer. However, this prop also prevents the site from being scrollable, which is not desired, especially
      // in iframes. We therefore override that method to disable the scroll prevention.
      if (typeof wsDrawerEl.value?.__preventScroll === 'function') {
        const temp = wsDrawerEl.value.__preventScroll;
        wsDrawerEl.value.__preventScroll = () => {
          temp(false);
        };
      }

      document.addEventListener('keydown', handleOnKeydown);
    });

    onBeforeUnmount(() => {
      document.removeEventListener('keydown', handleOnKeydown);
    });

    return {
      listeners,
      handleDrawerVisibility,
      WsButtonEnum,
      wsDrawerEl
    };
  }

});
</script>

<style lang="scss" scoped>
.ws-drawer {
  ::v-deep {
    .q-drawer {
      background: $drawer-bg;
      transition: transform 0.3s ease 0s;
      @media (max-width: 630px) {
        // 630px = 580drawer_width + 32close_btn + 12scrollbar + 6reserve
        // (different env might have different scrollbar width)
        width: calc(100% - #{map-get($space-xl, x)}) !important;
      }

      .q-layout__shadow {
        left: 0;
        box-shadow: $ws-box-shadow9;

        &:after {
          box-shadow: unset;
        }
      }
    }

    .q-drawer__content {
      padding: map-get($space-4xl, y) map-get($space-3xl, x)
        map-get($space-lg, y);
      @media (max-width: $ws-breakpoint-mobile-max) {
        padding: map-get($space-lg, x);
      }
    }

    .q-form {
      width: 100%;
    }
  }

  .ws-drawer-inner {
    .close-wrap {
      position: absolute;
      left: -21px;
      top: 25px;
      z-index: 30;
      background: $drawer-bg;
      border-radius: $button-border-radius;

      ::v-deep {
        .q-btn__wrapper {
          color: $drawer-close-btn-color;
          min-height: auto;
          padding: map-get($space-sm, x);
        }
      }
    }

    > .header {
      box-shadow: 0 0 24px 24px $drawer-bg;
      z-index: 20;
    }

    > .content {
      width: 100%;
    }

    > .footer {
      box-shadow: 0 0 24px 24px $drawer-bg;
      z-index: 20;
    }

    &.full-height-content {
      display: grid;
    }
  }
}
</style>
